import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useStore } from "react-redux";
import { useAppDispatch, type RootState } from "@app/store";
import { makeGql } from "@app/utils/gql";
import { fetchAllPages, type CursorPage } from "@app/utils/fetch-all-pages";
import { DEFAULT_HOTEL_POLICY_CONFIG, getMockCurrencyRates, MOCK_MILEAGE_POLICY_RULES } from "@app/pages/policy/policy-mock-data";
import { LOST_RECEIPT_ESCALATION_LEVEL, LOST_RECEIPT_ESCALATION_LEVELS, POLICY_AUTOMATIC, POLICY_STATUS, RECEIPT_RETENTION_OPTIONS } from "@app/types/policy";
import type { LostReceiptEscalationLevel } from "@app/types/policy";
import { CATEGORY_STATUS } from "@app/types/categories";
import type { CategoryStatus } from "@app/types/categories";
import type {
    CurrencyRate,
    PoliciesPageInfo,
    ExceptionPolicyRule,
    HotelPolicyDataConfig,
    MileagePolicyRule,
    PolicyBaseCurrency,
    PolicyDataFieldConfig,
    ReceiptPolicyRule,
    ReceiptRetention,
} from "@app/types/policy";

// Re-export for convenience used by the page component
export type { PoliciesPageInfo };

const GRAPHQL_ENDPOINT = import.meta.env.VITE_GRAPHQL_ENDPOINT ?? "";
const APPSYNC_API_KEY = import.meta.env.VITE_GRAPHQL_API_KEY ?? "";

// ─── GraphQL documents (wire up when backend endpoints are available) ─────────

const EXCEPTION_POLICIES_PAGE_LIMIT = 10;

const GET_EXCEPTION_POLICIES_QUERY = `
  query GetExceptionPolicies($limit: Int, $cursor: String, $filter: PoliciesFilter) {
    policies(limit: $limit, cursor: $cursor, filter: $filter) {
      items {
        policyId
        name
        status
        rules
        actions
      }
      pageInfo {
        totalCount
        cursor
        limit
        hasNextPage
      }
    }
  }
`;

const POLICY_FETCH_PAGE_LIMIT = 100;

const GET_RECEIPT_POLICIES_QUERY = `
  query GetReceiptPolicies($limit: Int, $cursor: String, $filter: PoliciesFilter) {
    policies(limit: $limit, cursor: $cursor, filter: $filter) {
      items {
        policyId
        name
        status
        rules
        updatedAt
        updatedBy {
          userName
        }
      }
      pageInfo {
        totalCount
        cursor
        limit
        hasNextPage
      }
    }
  }
`;

const MILEAGE_POLICY_RULES_QUERY = `
  query MileagePolicyRules {
    mileagePolicyRules {
      id
      ruleName
      status
      rate
      unit
      updatedBy
      changedDate
    }
  }
`;

const CURRENCY_POLICY_RATES_QUERY = `
  query CurrencyPolicyRates($baseCurrency: String!, $rateDate: String!) {
    currencyPolicyRates(baseCurrency: $baseCurrency, rateDate: $rateDate) {
      id
      currency
      currencyCode
      exchangeRate
    }
  }
`;

const HOTEL_POLICY_CONFIG_QUERY = `
  query HotelPolicyConfig {
    hotelPolicyConfig {
      recommendedFields {
        id
        label
        enabled
        fieldType
        requirement
        description
      }
      additionalFields {
        id
        label
        enabled
        fieldType
        requirement
        description
      }
      personalNonReimbursable {
        id
        label
        enabled
        fieldType
        requirement
        description
      }
      outsideHomeCountry
      vatFields {
        id
        label
        enabled
        fieldType
        requirement
        description
      }
    }
  }
`;

const UPDATE_HOTEL_POLICY_CONFIG_MUTATION = `
  mutation UpdateHotelPolicyConfig($input: HotelPolicyConfigInput!) {
    updateHotelPolicyConfig(input: $input) {
      recommendedFields {
        id
        label
        enabled
        fieldType
        requirement
        description
      }
      additionalFields {
        id
        label
        enabled
        fieldType
        requirement
        description
      }
      personalNonReimbursable {
        id
        label
        enabled
        fieldType
        requirement
        description
      }
      outsideHomeCountry
      vatFields {
        id
        label
        enabled
        fieldType
        requirement
        description
      }
    }
  }
`;

// Prevent unused-document lint noise until GraphQL is wired up.
void MILEAGE_POLICY_RULES_QUERY;
void CURRENCY_POLICY_RATES_QUERY;
void HOTEL_POLICY_CONFIG_QUERY;
void UPDATE_HOTEL_POLICY_CONFIG_MUTATION;

// ─── Response types ───────────────────────────────────────────────────────────

// ─── Exception policies types ────────────────────────────────────────────────

export type ExceptionPoliciesSortBy = "name";
export type ExceptionPoliciesSortOrder = "asc" | "desc";

export interface ExceptionPoliciesColumnFilter {
    name?: string;
    status?: string;
}

export interface GqlPolicy {
    policyId: string;
    name: string;
    description?: string;
    status: string;
    rules: string;
    actions: string[];
}

// ─── CEL expression parser ────────────────────────────────────────────────────
// Parses CEL strings stored in the backend into human-readable display values
// for the table columns. Field names: expense.amount, expense.mealParticipantCount,
// expense.entrySource (camelCase values: corporateCardMatched, manualOutOfPocket,
// travelBookingFeed), expense.hasBillable.

const CEL_ENTRY_SOURCE_LABELS: Record<string, string> = {
    corporateCardMatched: "Corp card",
    manualOutOfPocket: "Manual",
    travelBookingFeed: "Travel feed",
};

function formatCelAmount(val: number): string {
    return val % 1 === 0 ? val.toLocaleString("en-US") : val.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

interface ParsedCelInfo {
    expression: string;
    amount: string;
    entryType: string;
}

function parseCelExpression(cel: string): ParsedCelInfo {
    let expression = "";
    let amount = "";
    let entryType = "";

    // Between: expense.amount >= MIN && expense.amount <= MAX
    const betweenMatch = cel.match(/expense\.amount\s*>=\s*([\d.]+)\s*&&\s*expense\.amount\s*<=\s*([\d.]+)/);
    if (betweenMatch) {
        expression = "Between";
        const min = parseFloat(betweenMatch[1]);
        const max = parseFloat(betweenMatch[2]);
        amount = `$${formatCelAmount(min)} - $${formatCelAmount(max)}`;
    } else {
        // Single comparison: expense.amount > 5000.0  |  >= < <= ==
        const amountMatch = cel.match(/expense\.amount\s*(>=|<=|>|<|==)\s*([\d.]+)/);
        if (amountMatch) {
            const opLabels: Record<string, string> = {
                ">": "Greater than",
                ">=": "Greater than or equal",
                "<": "Less than",
                "<=": "Less than or equal",
                "==": "Equal to",
            };
            expression = opLabels[amountMatch[1]] ?? amountMatch[1];
            amount = `$${formatCelAmount(parseFloat(amountMatch[2]))}`;
        }
    }

    // Entry source — match all occurrences of expense.entrySource == "..."
    const entrySourceMatches = [...cel.matchAll(/expense\.entrySource\s*==\s*"([^"]+)"/g)].map((m) => m[1]);
    if (entrySourceMatches.length > 0) {
        entryType = entrySourceMatches.map((s) => CEL_ENTRY_SOURCE_LABELS[s] ?? s).join(", ");
    }

    return { expression, amount, entryType };
}

function mapGqlPolicyToExceptionRule(policy: GqlPolicy, categoryMap: Map<string, { categoryName: string; status: CategoryStatus }>): ExceptionPolicyRule {
    let category = "";
    let categoryInactive = false;
    let expression = "";
    let amount = "";
    let entryType = "";
    try {
        const rules = JSON.parse(policy.rules) as { categoryIds: string[]; expression: string };
        const { categoryIds } = rules;
        if (categoryIds.length === 1 && categoryIds[0] === "*") {
            category = "All categories";
        } else if (categoryIds.length === 1) {
            const resolved = categoryMap.get(categoryIds[0]);
            if (!resolved) {
                category = "Unknown";
            } else {
                category = resolved.categoryName;
                categoryInactive = resolved.status !== CATEGORY_STATUS.Active;
            }
        } else {
            const names = categoryIds.map((id) => {
                const resolved = categoryMap.get(id);
                if (!resolved) return null;
                if (resolved.status !== CATEGORY_STATUS.Active) categoryInactive = true;
                return resolved.categoryName;
            });
            const known = names.filter(Boolean) as string[];
            const unknownCount = names.filter((n) => n === null).length;
            category = known.length > 0 ? (unknownCount > 0 ? `${known.join(", ")} +${unknownCount} unknown` : known.join(", ")) : "Unknown";
        }
        const parsed = parseCelExpression(rules.expression);
        expression = parsed.expression;
        amount = parsed.amount;
        entryType = parsed.entryType;
    } catch {
        // leave empty if AWSJSON parse fails
    }

    let response = "";
    try {
        if (policy.actions.length > 0) {
            const firstAction = JSON.parse(policy.actions[0]) as { type: string };
            response = firstAction.type === "requireExplanation" ? "Require explanation" : "Highlight";
        }
    } catch {
        // leave empty if AWSJSON parse fails
    }

    return {
        id: policy.policyId,
        ruleName: policy.name,
        status: policy.status === "active" ? POLICY_STATUS.Active : POLICY_STATUS.Deactivated,
        category,
        categoryInactive,
        expression,
        amount,
        entryType,
        response,
    };
}

interface ReceiptGqlPolicy {
    policyId: string;
    name: string;
    status: string;
    rules: string;
    updatedAt?: string | null;
    updatedBy?: { userName?: string | null } | null;
}

interface ReceiptPoliciesQueryResponse {
    policies: {
        items: ReceiptGqlPolicy[];
        pageInfo: PoliciesPageInfo;
    };
}

const RECEIPT_POLICIES_PAGE_LIMIT = 10;

export type ReceiptPoliciesSortBy = "name";
export type ReceiptPoliciesSortOrder = "asc" | "desc";

export interface ReceiptPoliciesColumnFilter {
    name?: string;
    status?: string;
}

/**
 * Extracts the configured lost-receipt escalation level ("off" | "initial" | "explanation") from a receipt
 * policy's rules JSON, i.e. `rules.params.receiptCollectionRules.lostReceiptProtocol.escalationLevel`.
 * Defaults to "off" when absent, malformed, or not a recognized value.
 */
export function parseLostReceiptEscalationLevel(rulesJson: string): LostReceiptEscalationLevel {
    try {
        const rules = JSON.parse(rulesJson) as {
            params?: { receiptCollectionRules?: { lostReceiptProtocol?: { escalationLevel?: string } } };
        };
        const escalationLevel = rules.params?.receiptCollectionRules?.lostReceiptProtocol?.escalationLevel;
        if (typeof escalationLevel === "string" && (LOST_RECEIPT_ESCALATION_LEVELS as readonly string[]).includes(escalationLevel)) {
            return escalationLevel as LostReceiptEscalationLevel;
        }
    } catch {
        // leave default if AWSJSON parse fails
    }
    return LOST_RECEIPT_ESCALATION_LEVEL.Off;
}

/** Extracts the "require receipt at or above" amount (e.g. "$50.00") from a receipt policy's rules JSON; supports legacy ">" expressions. */
export function parseReceiptRequiredAmount(rulesJson: string): string {
    try {
        const rules = JSON.parse(rulesJson) as { expression?: string };
        const match = rules.expression?.match(/expense\.amount\s*>=?\s*([\d.]+)/);
        if (match) {
            return `$${formatCelAmount(parseFloat(match[1]))}`;
        }
    } catch {
        // leave empty if AWSJSON parse fails
    }
    return "";
}

function mapGqlPolicyToReceiptRule(policy: ReceiptGqlPolicy): ReceiptPolicyRule {
    const requiredAmount = parseReceiptRequiredAmount(policy.rules);
    let retainReceiptsFor: ReceiptRetention = "1 Year";
    let allowMultipleImageUploads = false;
    try {
        const rules = JSON.parse(policy.rules) as { retentionPeriod?: string; allowMultipleUploads?: boolean };
        if (typeof rules.retentionPeriod === "string" && (RECEIPT_RETENTION_OPTIONS as readonly string[]).includes(rules.retentionPeriod)) {
            retainReceiptsFor = rules.retentionPeriod as ReceiptRetention;
        }
        if (typeof rules.allowMultipleUploads === "boolean") {
            allowMultipleImageUploads = rules.allowMultipleUploads;
        }
    } catch {
        // leave defaults if AWSJSON parse fails
    }

    const changedDate = policy.updatedAt ?? "";

    return {
        id: policy.policyId,
        ruleName: policy.name,
        status: policy.status === "active" ? POLICY_STATUS.Active : POLICY_STATUS.Deactivated,
        requiredAmount,
        updatedBy: policy.updatedBy?.userName ?? "",
        changedDate,
        // Fields retained for type compatibility; not surfaced in the V1 receipt UI.
        lostProtocol: "",
        automatic: POLICY_AUTOMATIC.No,
        requireReceiptAtOrAbove: requiredAmount,
        retainReceiptsFor,
        allowMultipleImageUploads,
        lostReceiptEscalationLevel: parseLostReceiptEscalationLevel(policy.rules),
    };
}

interface MileagePolicyRulesResponse {
    mileagePolicyRules: MileagePolicyRule[];
}

interface CurrencyPolicyRatesResponse {
    currencyPolicyRates: CurrencyRate[];
}

interface HotelPolicyConfigResponse {
    hotelPolicyConfig: HotelPolicyDataConfig;
}

interface UpdateHotelPolicyConfigResponse {
    updateHotelPolicyConfig: HotelPolicyDataConfig;
}

// ─── Shared helpers ───────────────────────────────────────────────────────────

function useApiHeaders() {
    return useCallback((): Record<string, string> => {
        const headers: Record<string, string> = {};
        if (APPSYNC_API_KEY) headers["x-api-key"] = APPSYNC_API_KEY;
        return headers;
    }, []);
}

function usePolicyGql() {
    const dispatch = useAppDispatch();
    const store = useStore<RootState>();
    const getHeaders = useApiHeaders();
    return useCallback(makeGql(dispatch, store, getHeaders, GRAPHQL_ENDPOINT), [dispatch, store, getHeaders]);
}

function cloneHotelPolicyConfig(config: HotelPolicyDataConfig): HotelPolicyDataConfig {
    return {
        ...config,
        recommendedFields: config.recommendedFields.map((field) => ({ ...field })),
        additionalFields: config.additionalFields.map((field) => ({ ...field })),
        personalNonReimbursable: { ...config.personalNonReimbursable },
        vatFields: config.vatFields.map((field) => ({ ...field })),
    };
}

function updateFieldList(fields: PolicyDataFieldConfig[], id: string, patch: Partial<PolicyDataFieldConfig>): PolicyDataFieldConfig[] {
    return fields.map((field) => (field.id === id ? { ...field, ...patch } : field));
}

function usePolicyData<T>(loadData: () => Promise<T>, deps: unknown[], initialData?: T) {
    const [data, setData] = useState<T | null>(initialData ?? null);
    const [isLoading, setIsLoading] = useState(initialData === undefined);
    const [error, setError] = useState<string | null>(null);
    const latestRequestIdRef = useRef(0);
    const isFirstLoadRef = useRef(true);

    const load = useCallback(
        async (options?: { showLoadingIndicator?: boolean }) => {
            const requestId = ++latestRequestIdRef.current;
            if (options?.showLoadingIndicator !== false) {
                setIsLoading(true);
            }
            setError(null);

            try {
                const nextData = await loadData();
                if (requestId !== latestRequestIdRef.current) return;
                setData(nextData);
            } catch (err) {
                if (requestId !== latestRequestIdRef.current) return;
                setError(err instanceof Error ? err.message : "Unexpected error loading policy data");
            } finally {
                if (requestId === latestRequestIdRef.current) {
                    setIsLoading(false);
                }
            }
        },
        [loadData],
    );

    const lastLoadRef = useRef<typeof load | null>(null);
    const hasFetchedRef = useRef(false);

    useEffect(() => {
        // Reset the guard whenever load changes (real dep change → re-fetch).
        // Keeps the same ref value across Strict Mode's fake unmount/remount so
        // the second effect invocation is skipped and only one request fires.
        if (lastLoadRef.current !== load) {
            lastLoadRef.current = load;
            hasFetchedRef.current = false;
        }
        if (hasFetchedRef.current) return;
        hasFetchedRef.current = true;

        const showLoadingIndicator = !isFirstLoadRef.current || initialData === undefined;
        isFirstLoadRef.current = false;
        void load({ showLoadingIndicator });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [load, ...deps]);

    const refresh = useCallback(() => {
        void load({ showLoadingIndicator: true });
    }, [load]);

    return { data, isLoading, error, refresh };
}

// ─── List hooks ───────────────────────────────────────────────────────────────

interface PoliciesColumnFilter {
    name?: string;
    status?: string;
}

interface PoliciesQueryFilter extends PoliciesColumnFilter {
    sort?: string;
    policyType?: string;
}

interface PaginatedPoliciesResponse<TItem> {
    policies: {
        items: TItem[];
        pageInfo: PoliciesPageInfo;
    };
}

interface CursorPaginatedPoliciesOptions {
    /** GraphQL document returning `policies { items pageInfo }`. */
    query: string;
    policyType: string;
    pageLimit: number;
    errorMessage: string;
}

/**
 * Shared cursor-based, server-paginated policies loader. Owns fetching, cursor history,
 * request de-duplication/race-guarding, and filter/sort/page state; callers map the raw
 * `items` to their view model. Backs {@link useExceptionPolicies} and {@link useReceiptPolicies}.
 */
function useCursorPaginatedPolicies<TItem, TSortBy extends string>(options: CursorPaginatedPoliciesOptions) {
    const gql = usePolicyGql();

    const [items, setItems] = useState<TItem[]>([]);
    const [pageInfo, setPageInfo] = useState<PoliciesPageInfo | null>(null);
    const [currentPage, setCurrentPage] = useState(0);
    const [sort, setSort] = useState<{ sortBy: TSortBy; sortOrder: "asc" | "desc" } | null>(null);
    const [columnFilter, setColumnFilter] = useState<PoliciesColumnFilter>({});
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const cursorHistoryRef = useRef<Array<string | null>>([null]);
    const inflightRef = useRef<Set<string>>(new Set());
    const latestRequestIdRef = useRef(0);
    const firstMountRef = useRef(true);

    const sortRef = useRef(sort);
    const columnFilterRef = useRef(columnFilter);
    sortRef.current = sort;
    columnFilterRef.current = columnFilter;

    const optionsRef = useRef(options);
    optionsRef.current = options;

    const loadPage = useCallback(
        async (page: number) => {
            const { query, policyType, pageLimit, errorMessage } = optionsRef.current;
            const cursor = cursorHistoryRef.current[page] ?? null;
            const currentSort = sortRef.current;
            const currentColumnFilter = columnFilterRef.current;
            const key = `page-${page}-cursor-${cursor ?? "null"}-sort-${JSON.stringify(currentSort)}-filter-${JSON.stringify(currentColumnFilter)}`;

            if (inflightRef.current.has(key)) return;
            inflightRef.current.add(key);

            const requestId = ++latestRequestIdRef.current;
            setIsLoading(true);
            setError(null);

            const filter: PoliciesQueryFilter = {
                policyType,
                ...(currentColumnFilter.name ? { name: currentColumnFilter.name } : {}),
                ...(currentColumnFilter.status ? { status: currentColumnFilter.status } : {}),
                ...(currentSort ? { sort: `${currentSort.sortBy}:${currentSort.sortOrder}` } : {}),
            };

            try {
                const data = await gql<PaginatedPoliciesResponse<TItem>>(query, { limit: pageLimit, cursor, filter });

                if (requestId !== latestRequestIdRef.current) return;

                const { items: pageItems, pageInfo: pi } = data.policies;
                setItems(pageItems);
                setPageInfo(pi);

                if (pi.hasNextPage && pi.cursor) {
                    cursorHistoryRef.current[page + 1] = pi.cursor;
                }
            } catch (err) {
                if (requestId !== latestRequestIdRef.current) return;
                setError(err instanceof Error ? err.message : errorMessage);
            } finally {
                inflightRef.current.delete(key);
                if (requestId === latestRequestIdRef.current) {
                    setIsLoading(false);
                }
            }
        },
        [gql],
    );

    // Initial load
    useEffect(() => {
        void loadPage(0);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Reload when page, sort, or filter changes (skip first mount)
    useEffect(() => {
        if (firstMountRef.current) {
            firstMountRef.current = false;
            return;
        }
        void loadPage(currentPage);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentPage, sort, columnFilter]);

    const setPage = useCallback((page: number) => {
        setCurrentPage(page);
    }, []);

    const setSortExternal = useCallback((next: { sortBy: TSortBy; sortOrder: "asc" | "desc" } | null) => {
        sortRef.current = next;
        cursorHistoryRef.current = [null];
        setCurrentPage(0);
        setSort(next);
    }, []);

    const setColumnFilterExternal = useCallback((next: PoliciesColumnFilter) => {
        columnFilterRef.current = next;
        cursorHistoryRef.current = [null];
        setCurrentPage(0);
        setColumnFilter(next);
    }, []);

    const refresh = useCallback(() => {
        cursorHistoryRef.current = [null];
        setCurrentPage(0);
        void loadPage(0);
    }, [loadPage]);

    return {
        items,
        pageInfo,
        currentPage,
        sort,
        isLoading,
        error,
        setPage,
        setSort: setSortExternal,
        setColumnFilter: setColumnFilterExternal,
        refresh,
    };
}

export function useExceptionPolicies(categoryMap: Map<string, { categoryName: string; status: CategoryStatus }>) {
    const { items, ...rest } = useCursorPaginatedPolicies<GqlPolicy, ExceptionPoliciesSortBy>({
        query: GET_EXCEPTION_POLICIES_QUERY,
        policyType: "exceptions",
        pageLimit: EXCEPTION_POLICIES_PAGE_LIMIT,
        errorMessage: "Unexpected error loading exception policies",
    });

    // Re-map whenever items or categoryMap change (categories can load after the policies).
    const policies = useMemo(() => items.map((p) => mapGqlPolicyToExceptionRule(p, categoryMap)), [items, categoryMap]);

    return { policies, ...rest };
}

export function useReceiptPolicyRules() {
    const gql = usePolicyGql();

    const loadRules = useCallback(async () => {
        const policies = await fetchAllPages((cursor) =>
            gql<ReceiptPoliciesQueryResponse>(GET_RECEIPT_POLICIES_QUERY, {
                limit: POLICY_FETCH_PAGE_LIMIT,
                cursor,
                filter: { policyType: "receipt" },
            }).then((data) => data.policies),
        );
        return policies.map(mapGqlPolicyToReceiptRule);
    }, [gql]);

    const { data, isLoading, error, refresh } = usePolicyData<ReceiptPolicyRule[]>(loadRules, []);

    // Memoize so the returned array keeps a stable reference while `data` is unchanged
    // (including the initial `null` window). Without this, `data ?? []` yields a new
    // array every render, which drives consumers that sync it via effect into an
    // infinite render loop.
    const rules = useMemo(() => data ?? [], [data]);

    return {
        rules,
        isLoading,
        error,
        refresh,
    };
}

// Server-side, cursor-paginated receipt policies for the /policy/receipt table
// (10 per page; Next/Prev re-query the endpoint).
export function useReceiptPolicies() {
    const { items, ...rest } = useCursorPaginatedPolicies<ReceiptGqlPolicy, ReceiptPoliciesSortBy>({
        query: GET_RECEIPT_POLICIES_QUERY,
        policyType: "receipt",
        pageLimit: RECEIPT_POLICIES_PAGE_LIMIT,
        errorMessage: "Unexpected error loading receipt policies",
    });

    const policies = useMemo(() => items.map(mapGqlPolicyToReceiptRule), [items]);

    return { policies, ...rest };
}

export function useMileagePolicyRules() {
    const gql = usePolicyGql();

    const loadRules = useCallback(async () => {
        // const response = await gql<MileagePolicyRulesResponse>(MILEAGE_POLICY_RULES_QUERY);
        // return response.mileagePolicyRules;
        void gql;
        void ({} as MileagePolicyRulesResponse);
        return MOCK_MILEAGE_POLICY_RULES;
    }, [gql]);

    const { data, isLoading, error, refresh } = usePolicyData(loadRules, [], MOCK_MILEAGE_POLICY_RULES);

    return {
        rules: data ?? [],
        isLoading,
        error,
        refresh,
    };
}

export interface CurrencyPolicyRatesOptions {
    baseCurrency: PolicyBaseCurrency;
    rateDate: string;
}

export function useCurrencyPolicyRates({ baseCurrency, rateDate }: CurrencyPolicyRatesOptions) {
    const gql = usePolicyGql();

    const loadRates = useCallback(async () => {
        // const response = await gql<CurrencyPolicyRatesResponse>(CURRENCY_POLICY_RATES_QUERY, { baseCurrency, rateDate });
        // return response.currencyPolicyRates;
        void gql;
        void ({} as CurrencyPolicyRatesResponse);
        return getMockCurrencyRates(baseCurrency, rateDate);
    }, [baseCurrency, gql, rateDate]);

    const { data, isLoading, error, refresh } = usePolicyData(loadRates, [baseCurrency, rateDate], getMockCurrencyRates(baseCurrency, rateDate));

    return {
        rates: data ?? [],
        isLoading,
        error,
        refresh,
    };
}

// ─── Hotel policy config ──────────────────────────────────────────────────────

export function useHotelPolicyConfig() {
    const gql = usePolicyGql();

    const [baseline, setBaseline] = useState<HotelPolicyDataConfig>(() => cloneHotelPolicyConfig(DEFAULT_HOTEL_POLICY_CONFIG));
    const [config, setConfig] = useState<HotelPolicyDataConfig>(() => cloneHotelPolicyConfig(DEFAULT_HOTEL_POLICY_CONFIG));
    const [isLoading, setIsLoading] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const latestRequestIdRef = useRef(0);
    const isMountedRef = useRef(true);
    const isFirstLoadRef = useRef(true);

    useEffect(() => {
        isMountedRef.current = true;
        return () => {
            isMountedRef.current = false;
        };
    }, []);

    const loadConfig = useCallback(async () => {
        const requestId = ++latestRequestIdRef.current;
        const showLoadingIndicator = !isFirstLoadRef.current;
        isFirstLoadRef.current = false;
        if (showLoadingIndicator) {
            setIsLoading(true);
        }
        setError(null);

        try {
            // const response = await gql<HotelPolicyConfigResponse>(HOTEL_POLICY_CONFIG_QUERY);
            // const nextConfig = cloneHotelPolicyConfig(response.hotelPolicyConfig);
            void gql;
            void ({} as HotelPolicyConfigResponse);
            const nextConfig = cloneHotelPolicyConfig(DEFAULT_HOTEL_POLICY_CONFIG);

            if (requestId !== latestRequestIdRef.current || !isMountedRef.current) return;

            setBaseline(nextConfig);
            setConfig(cloneHotelPolicyConfig(nextConfig));
        } catch (err) {
            if (requestId !== latestRequestIdRef.current || !isMountedRef.current) return;
            setError(err instanceof Error ? err.message : "Unexpected error loading hotel policy");
        } finally {
            if (requestId === latestRequestIdRef.current && isMountedRef.current) {
                setIsLoading(false);
            }
        }
    }, [gql]);

    useEffect(() => {
        void loadConfig();
    }, [loadConfig]);

    const hasChanges = useMemo(() => JSON.stringify(config) !== JSON.stringify(baseline), [baseline, config]);

    const updateRecommendedField = useCallback((id: string, patch: Partial<PolicyDataFieldConfig>) => {
        setConfig((current) => ({
            ...current,
            recommendedFields: updateFieldList(current.recommendedFields, id, patch),
        }));
    }, []);

    const updateAdditionalField = useCallback((id: string, patch: Partial<PolicyDataFieldConfig>) => {
        setConfig((current) => ({
            ...current,
            additionalFields: updateFieldList(current.additionalFields, id, patch),
        }));
    }, []);

    const updateVatField = useCallback((id: string, patch: Partial<PolicyDataFieldConfig>) => {
        setConfig((current) => ({
            ...current,
            vatFields: updateFieldList(current.vatFields, id, patch),
        }));
    }, []);

    const setOutsideHomeCountry = useCallback((outsideHomeCountry: boolean) => {
        setConfig((current) => ({ ...current, outsideHomeCountry }));
    }, []);

    const updatePersonalNonReimbursable = useCallback((patch: Partial<PolicyDataFieldConfig>) => {
        setConfig((current) => ({
            ...current,
            personalNonReimbursable: { ...current.personalNonReimbursable, ...patch },
        }));
    }, []);

    const cancelChanges = useCallback(() => {
        setConfig(cloneHotelPolicyConfig(baseline));
    }, [baseline]);

    const saveChanges = useCallback(async () => {
        setIsSaving(true);
        setError(null);

        try {
            // const response = await gql<UpdateHotelPolicyConfigResponse>(UPDATE_HOTEL_POLICY_CONFIG_MUTATION, { input: config });
            // const savedConfig = cloneHotelPolicyConfig(response.updateHotelPolicyConfig);
            void gql;
            void ({} as UpdateHotelPolicyConfigResponse);
            const savedConfig = cloneHotelPolicyConfig(config);

            if (!isMountedRef.current) return;

            setBaseline(savedConfig);
            setConfig(cloneHotelPolicyConfig(savedConfig));
        } catch (err) {
            if (!isMountedRef.current) return;
            setError(err instanceof Error ? err.message : "Failed to save hotel policy");
            throw err;
        } finally {
            if (isMountedRef.current) {
                setIsSaving(false);
            }
        }
    }, [config, gql]);

    const refresh = useCallback(() => {
        isFirstLoadRef.current = false;
        void loadConfig();
    }, [loadConfig]);

    return {
        config,
        hasChanges,
        isLoading,
        isSaving,
        error,
        updateRecommendedField,
        updateAdditionalField,
        updateVatField,
        setOutsideHomeCountry,
        updatePersonalNonReimbursable,
        cancelChanges,
        saveChanges,
        refresh,
    };
}

// ─── Create policy mutation ───────────────────────────────────────────────────

const GET_POLICY_BY_ID_QUERY = `
  query GetPolicyById($policyId: ID!) {
    getPolicyById(policyId: $policyId) {
      policyId
      name
      description
      status
      rules
      actions
    }
  }
`;

const UPDATE_POLICY_MUTATION = `
  mutation UpdatePolicy($policyId: ID!, $input: UpdatePolicyInput!) {
    updatePolicy(policyId: $policyId, input: $input) {
      policyId
      name
      status
    }
  }
`;

const CREATE_POLICY_MUTATION = `
  mutation CreatePolicy($input: CreatePolicyInput!) {
    createPolicy(input: $input) {
      policyId
      domain
      policyType
      name
      description
      status
      rules
      actions
      createdAt
      updatedAt
      createdBy {
        userId
        userName
      }
    }
  }
`;

export interface LostReceiptProtocolParams {
    escalationLevel: LostReceiptEscalationLevel;
}

export interface ReceiptCollectionRulesParams {
    lostReceiptProtocol?: LostReceiptProtocolParams;
}

export interface CreatePolicyRulesParams {
    receiptCollectionRules?: ReceiptCollectionRulesParams;
}

export interface CreatePolicyRules {
    type: "cel";
    categoryIds: string[];
    excludeCategoryIds?: string[];
    expression: string;
    retentionPeriod?: string;
    allowMultipleUploads?: boolean;
    params?: CreatePolicyRulesParams;
}

export interface CreatePolicyInput {
    name: string;
    description?: string;
    status: "active" | "inactive";
    rules: CreatePolicyRules;
    actions: Array<{ type: string }>;
    policyType?: string;
}

export interface UpdatePolicyInput {
    name: string;
    description?: string;
    status: "active" | "inactive";
    rules: CreatePolicyRules;
    actions: Array<{ type: string }>;
    policyType?: string;
}

interface CreatedPolicy {
    policyId: string;
    domain: string;
    policyType: string;
    name: string;
    description?: string;
    status: string;
    rules: unknown;
    actions: unknown;
    createdAt: string;
    updatedAt: string;
    createdBy?: { userId: string; userName: string };
}

interface CreatePolicyResponse {
    createPolicy: CreatedPolicy;
}

export function useCreatePolicy() {
    const gql = usePolicyGql();
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const createPolicy = useCallback(
        async (input: CreatePolicyInput): Promise<CreatedPolicy> => {
            setIsLoading(true);
            setError(null);
            try {
                const serializedInput = {
                    ...input,
                    rules: JSON.stringify(input.rules),
                    actions: input.actions.map((action) => JSON.stringify(action)),
                };
                const response = await gql<CreatePolicyResponse>(CREATE_POLICY_MUTATION, { input: serializedInput });
                return response.createPolicy;
            } catch (err) {
                const message = err instanceof Error ? err.message : "Failed to create policy rule";
                setError(message);
                throw err;
            } finally {
                setIsLoading(false);
            }
        },
        [gql],
    );

    return { createPolicy, isLoading, error };
}

interface GetPolicyByIdResponse {
    getPolicyById: GqlPolicy;
}

export function useGetPolicyById() {
    const gql = usePolicyGql();

    const getPolicyById = useCallback(
        async (policyId: string): Promise<GqlPolicy | null> => {
            const response = await gql<GetPolicyByIdResponse>(GET_POLICY_BY_ID_QUERY, { policyId });
            return response.getPolicyById ?? null;
        },
        [gql],
    );

    return { getPolicyById };
}

interface UpdatePolicyResponse {
    updatePolicy: { policyId: string; name: string; status: string };
}

export function useUpdatePolicy() {
    const gql = usePolicyGql();
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const updatePolicy = useCallback(
        async (policyId: string, input: UpdatePolicyInput): Promise<void> => {
            setIsLoading(true);
            setError(null);
            try {
                const serializedInput = {
                    ...input,
                    rules: JSON.stringify(input.rules),
                    actions: input.actions.map((action) => JSON.stringify(action)),
                };
                await gql<UpdatePolicyResponse>(UPDATE_POLICY_MUTATION, { policyId, input: serializedInput });
            } catch (err) {
                const message = err instanceof Error ? err.message : "Failed to update policy rule";
                setError(message);
                throw err;
            } finally {
                setIsLoading(false);
            }
        },
        [gql],
    );

    return { updatePolicy, isLoading, error };
}

// ─── useGetPoliciesForGroup ───────────────────────────────────────────────────

const GET_POLICIES_FOR_GROUP_QUERY = `
    query GetPoliciesForGroup($limit: Int, $cursor: String, $filter: PoliciesFilter) {
        groupPolicies(limit: $limit, cursor: $cursor, filter: $filter) {
      items {
        policyId
        name
        status
        rules
      }
            pageInfo {
                cursor
                hasNextPage
            }
    }
  }
`;

export interface PolicyForGroup {
    policyId: string;
    name: string;
    status: "active" | "inactive" | "deleted";
    rules?: string;
}

export function useGetPoliciesForGroup(policyType: string = "exceptions") {
    const gql = usePolicyGql();
    const [policies, setPolicies] = useState<PolicyForGroup[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const fetchedRef = useRef(false);

    useEffect(() => {
        if (fetchedRef.current) return;
        fetchedRef.current = true;

        setIsLoading(true);
        setError(null);
        fetchAllPages((cursor) =>
            gql<{ groupPolicies: CursorPage<PolicyForGroup> }>(GET_POLICIES_FOR_GROUP_QUERY, {
                limit: POLICY_FETCH_PAGE_LIMIT,
                cursor,
                filter: { policyType, includeDeleted: true },
            }).then((data) => data.groupPolicies),
        )
            .then((policies) => {
                setPolicies(policies);
            })
            .catch((err) => {
                setError(err instanceof Error ? err.message : "Failed to load policies");
            })
            .finally(() => {
                setIsLoading(false);
            });
    }, [gql, policyType]);

    return { policies, isLoading, error };
}
